import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getInvoices,
  logActivity,
} from '../../services/adminMockData';
import {
  getRetailerById, setRetailerActive, setRetailerBlocked, adminUpdateShop, adminUpdateTokens,
  getAreaLookup, getAllDealsAdmin, isRemovedDeal, adminSetSubscription,
} from '../../services/apiService';
import LocationSearchPanel from '../../components/admin/LocationSearchPanel';
import { DEAL_CATEGORIES } from '../../constants/categories';


// Same list ShopProfilePage and the deal pages offer, so a branche set here
// matches the one a retailer can set for itself.
// Single platform-wide list — the walker feed filters on exactly these.
const CATEGORIES = DEAL_CATEGORIES;

const DAYS = [
  { key: 'ma',  label: 'Ma' },
  { key: 'di',  label: 'Di' },
  { key: 'wo',  label: 'Wo' },
  { key: 'do',  label: 'Do' },
  { key: 'vr',  label: 'Vr' },
  { key: 'za',  label: 'Za' },
  { key: 'zo',  label: 'Zo' },
];

const SUBSCRIPTION_PLAN_LABELS = {
  unlimited: 'Unlimited',
  growth: 'Growth',
  starter: 'Starter',
};

const DEFAULT_HOURS = {
  ma: { open: '09:00', close: '18:00', closed: false },
  di: { open: '09:00', close: '18:00', closed: false },
  wo: { open: '09:00', close: '18:00', closed: false },
  do: { open: '09:00', close: '18:00', closed: false },
  vr: { open: '09:00', close: '18:00', closed: false },
  za: { open: '10:00', close: '17:00', closed: false },
  zo: { open: '',      close: '',      closed: true  },
};

function updateShopInBoth(shopId, shopEmail, patch) {
  const emailLower = (shopEmail || '').toLowerCase();
  for (const key of ['walkin_shops', 'walkin_admin_shops']) {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch { list = []; }
    const next = list.map((s) => {
      const matches = s.id === shopId || (s.email || '').toLowerCase() === emailLower;
      return matches ? { ...s, ...patch } : s;
    });
    localStorage.setItem(key, JSON.stringify(next));
  }
  window.dispatchEvent(new Event('walkin-admin-shops-change'));
}

function generatePassword() {
  return 'WI-' + Math.random().toString(36).slice(2, 8).toUpperCase();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function formatDateTime(iso) {
  if (!iso) return 'Nog niet';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function emptyForm(shop) {
  return {
    shopName: shop?.shopName || '',
    email: shop?.email || '',
    area: shop?.area || '',
    address: shop?.address || '',
    postcode: shop?.postcode || '',
    city: shop?.city || '',
    description: shop?.description || '',
    category: shop?.category || '',
    instructions: shop?.instructions || '',
    lat: shop?.lat ?? null,
    lng: shop?.lng ?? null,
    locationVerified: !!shop?.locationVerified,
    locationAddress: shop?.locationAddress || '',
    subscription: shop?.subscription || 'tokens',
    // "Unlimited tot" (yyyy-mm-dd, optional) — e.g. the end of the pilot.
    unlimitedUntil: shop?.subscription === 'unlimited' && shop?.subscription_end_date
      ? String(shop.subscription_end_date).slice(0, 10) : '',
    tokensAvailable: shop?.tokensAvailable ?? 0,
    tokensPurchased: shop?.tokensPurchased ?? 0,
    status: shop?.status || 'actief',
    openingHours: shop?.openingHours || DEFAULT_HOURS,
  };
}

export default function AdminShopDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(() => emptyForm(null));
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal state
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdMode, setPwdMode] = useState('generated');
  const [pwdManual, setPwdManual] = useState('');
  const [pwdResult, setPwdResult] = useState(null);

  const [blockModal, setBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const [deleteStep, setDeleteStep] = useState(0); // 0 = closed, 1 = first confirm, 2 = type-to-confirm
  const [deleteTypeIn, setDeleteTypeIn] = useState('');

  const [showAreaModal, setShowAreaModal] = useState(false);
  const [areaChangeDeals, setAreaChangeDeals] = useState({ all: [], active: [], paused: [] });

  // Gebieden from the backend (id → area) — replaces the hardcoded name list,
  // and gives the gebiedscode/-adres shown on this page.
  const [areaLookup, setAreaLookup] = useState(() => new Map());
  // This shop's deals, from the backend (was: browser mock data).
  const [shopDeals, setShopDeals] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getAreaLookup().then((m) => { if (!cancelled) setAreaLookup(m); });
    return () => { cancelled = true; };
  }, []);

  const loadShop = async () => {
    setLoading(true);
    setError('');
    try {
      const found = await getRetailerById(id);
      setShop(found);
      if (!isEditing && found) setFormData(emptyForm(found));
    } catch (err) {
      // Reporting every failure as "Winkelier niet gevonden" hid the real
      // cause: an expired or missing admin token answers 401/403 here, which
      // looks identical to a retailer that genuinely does not exist.
      setShop(null);
      if (err?.status === 401) {
        setError('Je admin-sessie is verlopen. Log opnieuw in.');
      } else if (err?.status === 403) {
        setError('Dit account heeft geen adminrechten voor winkelierbeheer.');
      } else if (err?.status === 404) {
        setError('');
      } else {
        setError(err?.message || 'Kon winkelier niet laden.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!shop?.id) return undefined;
    let cancelled = false;
    getAllDealsAdmin()
      .then((all) => {
        if (cancelled) return;
        setShopDeals(all.filter((d) =>
          !isRemovedDeal(d) &&
          (d.retailerId === shop.id || (!d.retailerId && d.shopName === shop.shopName))
        ));
      })
      .catch(() => { if (!cancelled) setShopDeals([]); });
    return () => { cancelled = true; };
  }, [shop?.id, shop?.shopName, shop?.active]);

  const shopInvoices = useMemo(() => {
    if (!shop) return [];
    return getInvoices().filter((inv) => inv.shopId === shop.id);
  }, [shop]);

  if (loading) {
    return (
      <div>
        <header className="admin-page-head">
          <button className="admin-link" onClick={() => navigate('/admin/shops')}>
            ← Terug naar winkeliers
          </button>
        </header>
        <div className="admin-empty">Winkelier laden…</div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div>
        <header className="admin-page-head">
          <button className="admin-link" onClick={() => navigate('/admin/shops')}>
            ← Terug naar winkeliers
          </button>
        </header>
        <div className="admin-empty">
          {error || 'Winkelier niet gevonden.'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="admin-btn admin-btn--ghost" onClick={loadShop}>
            Opnieuw proberen
          </button>
          {error.includes('verlopen') && (
            <button
              className="admin-btn admin-btn--orange"
              onClick={() => {
                sessionStorage.removeItem('walkin_admin_mode');
                navigate('/admin/login');
              }}
            >
              Opnieuw inloggen
            </button>
          )}
        </div>
      </div>
    );
  }

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const setHour = (dayKey, patch) => {
    setFormData((f) => ({
      ...f,
      openingHours: {
        ...f.openingHours,
        [dayKey]: { ...(f.openingHours?.[dayKey] || { open: '', close: '', closed: false }), ...patch },
      },
    }));
  };

  const copy = (text, label) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      showToast(`${label} gekopieerd`);
    }
  };

  const handleEdit = () => {
    setFormData(emptyForm(shop));
    setError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setError('');
    setIsEditing(false);
    setFormData(emptyForm(shop));
  };

  const commitSave = async () => {
    const emailLower = formData.email.toLowerCase().trim();

    setSaving(true);
    setError('');

    // PUT /admin/shops/:id goes first and the browser mirror only follows a
    // successful write, so a failed save leaves the form open on the edit
    // instead of quietly banking it locally.
    let saved;
    try {
      saved = await adminUpdateShop(id, {
        shopName: formData.shopName.trim(),
        email: emailLower,
        area: formData.area,
        address: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        description: formData.description,
        category: formData.category,
        instructions: formData.instructions,
        openingHours: formData.openingHours,
        lat: formData.lat,
        lng: formData.lng,
      });
    } catch (err) {
      setSaving(false);
      if (err?.status === 401) {
        setError('Je admin-sessie is verlopen. Log opnieuw in.');
      } else if (err?.status === 403) {
        setError('Dit account heeft geen adminrechten om winkeliers te bewerken.');
      } else if (err?.status === 404) {
        setError('Deze winkelier staat niet in de database en kan niet bewerkt worden.');
      } else if (err?.status === 409) {
        setError('Dit e-mailadres is al in gebruik door een andere winkelier.');
      } else {
        setError(err?.message || 'Opslaan mislukt — er is niets gewijzigd.');
      }
      return;
    }
    // Tokens go to their own endpoint: the backend keeps a ledger of
    // purchased/used and derives the balance, while this form edits
    // purchased and available. `used` is therefore the difference — and a
    // balance larger than the amount purchased is not a state the ledger can
    // represent, so it is caught here rather than coming back as a 400.
    //
    // Unlimited has no integer form on the backend and stays browser-only,
    // so the ledger is left untouched for those shops.
    // The admin sets the BALANCE ("beschikbaar"). The ledger keeps
    // purchased/used, so `used` (what the shop really spent) is left alone and
    // `purchased` becomes used + the new balance. The old form derived `used`
    // from two editable fields and refused any balance above "gekocht", so
    // simply giving a shop more tokens was rejected and never reached the
    // database.
    // Unlimited / tokens — stored in the database (backend 0.3:
    // retailer_subscriptions). Only sent when the admin changed it.
    let savedPlan = null;
    const wasUntil = shop.subscription === 'unlimited' && shop.subscription_end_date
      ? String(shop.subscription_end_date).slice(0, 10) : '';
    const planChanged = formData.subscription !== (shop.subscription || 'tokens')
      || (formData.subscription === 'unlimited' && formData.unlimitedUntil !== wasUntil);
    if (planChanged) {
      try {
        savedPlan = await adminSetSubscription(id, {
          plan: formData.subscription,
          endsAt: formData.subscription === 'unlimited' && formData.unlimitedUntil
            ? new Date(`${formData.unlimitedUntil}T23:59:59`).toISOString()
            : null,
          source: 'admin',
        });
      } catch (err) {
        setSaving(false);
        setError(
          err?.status === 404
            ? 'Profiel opgeslagen, maar het abonnement niet: de backend heeft de Unlimited-uitbreiding (0.3) nog niet.'
            : `Profiel opgeslagen, maar het abonnement niet: ${err?.message || 'onbekende fout'}`
        );
        return;
      }
    }

    let savedTokens = null;
    const availableNow = Math.max(0, parseInt(formData.tokensAvailable, 10) || 0);
    if (formData.subscription !== 'unlimited' && availableNow !== Number(shop.tokensAvailable ?? 0)) {
      const available = availableNow;
      const used = Number(shop.tokensUsed) || 0;
      const purchased = used + available;

      try {
        savedTokens = await adminUpdateTokens(id, { purchased, used, reason: 'Saldo aangepast door admin' });
      } catch (err) {
        setSaving(false);
        // The profile write above already succeeded, so say which half failed
        // rather than implying the whole save was lost.
        setError(
          `Profiel opgeslagen, maar de tokens niet: ${err?.message || 'onbekende fout'}`
        );
        return;
      }
    }

    setSaving(false);

    // Abonnement and the status history have no backend columns, so those
    // still live in the browser mirror. Postcode and stad moved to the
    // database on 2026-09-19 and are saved above; tokens just went to the
    // ledger endpoint.
    const patch = {
      ...formData,
      email: emailLower,
      updatedAt: new Date().toISOString(),
      updatedBy: sessionStorage.getItem('walkin_admin_email') || 'admin',
    };
    if (formData.subscription === 'unlimited') {
      patch.tokensAvailable = 'unlimited';
      patch.tokensPurchased = 'unlimited';
    } else {
      patch.tokensAvailable = parseInt(formData.tokensAvailable, 10) || 0;
      patch.tokensPurchased = parseInt(formData.tokensPurchased, 10) || 0;
    }
    if (formData.status !== shop.status) {
      patch.statusChangedAt = new Date().toISOString();
      patch.statusChangedBy = sessionStorage.getItem('walkin_admin_email') || 'admin';
    }
    updateShopInBoth(id, shop.email, patch);
    logActivity('Winkelier bijgewerkt', {
      shop: patch.shopName,
      id,
      areaChange: shop.area !== formData.area ? `${shop.area} → ${formData.area}` : undefined,
    });

    // What came back from the database wins over what was typed, so the screen
    // shows what is actually stored. `status` is dropped from that: it is a
    // browser-only field here, and the value the backend derives from `active`
    // would silently undo the dropdown.
    const { status: _ignoredDbStatus, ...savedFields } = saved || {};
    setShop({ ...shop, ...patch, ...savedFields, ...(savedPlan || {}), ...(savedTokens || {}) });
    setIsEditing(false);
    // Re-read from the database so the screen shows what is stored, not
    // what was typed.
    loadShop();

    // Status is editable on this screen but has no column behind this endpoint
    // — blokkeren/deblokkeren is what actually persists it — so say so rather
    // than let a green tick cover for it.
    if (formData.status !== shop.status) {
      showToast('Opgeslagen — behalve status (gebruik blokkeren/deblokkeren)', 'error');
    } else if (emailLower !== (shop.email || '').toLowerCase().trim()) {
      // Worth calling out: the retailer has to sign in with the new address
      // from now on. Their current session survives — the JWT resolves the
      // user by id, not by e-mail.
      showToast('Opgeslagen — de winkelier logt voortaan in met het nieuwe e-mailadres');
    } else {
      showToast('Winkelier opgeslagen');
    }
  };

  const handleSave = async () => {
    if (!formData.shopName.trim() || !formData.email.trim()) {
      setError('Naam en e-mail zijn verplicht');
      return;
    }
    // Detect area change: if so, count existing deals and gate on user choice.
    if (formData.area !== shop.area) {
      const allDeals = (() => {
        try { return JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
        catch { return []; }
      })();
      const shopDeals = allDeals.filter(
        (d) =>
          (d.shopName === shop.shopName || d.createdBy === shop.shopName) &&
          d.status !== 'verwijderd'
      );
      if (shopDeals.length > 0) {
        setAreaChangeDeals({
          all: shopDeals,
          active: shopDeals.filter((d) => d.status === 'actief'),
          paused: shopDeals.filter((d) => d.status === 'gepauzeerd'),
        });
        setShowAreaModal(true);
        return;
      }
    }
    await commitSave();
  };

  const handleAreaChange = async (action) => {
    const oldArea = shop.area;
    const newArea = formData.area;
    let allDeals = [];
    try { allDeals = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
    catch { allDeals = []; }

    const matches = (d) => d.shopName === shop.shopName || d.createdBy === shop.shopName;
    const now = new Date().toISOString();
    let updatedDeals = allDeals;
    let toastMsg = '';

    if (action === 'move') {
      updatedDeals = allDeals.map((d) => matches(d) ? { ...d, area: newArea } : d);
      toastMsg = `✅ ${areaChangeDeals.all.length} deals verplaatst naar ${newArea}`;
    } else if (action === 'expire') {
      updatedDeals = allDeals.map((d) =>
        matches(d) && d.status !== 'verwijderd'
          ? { ...d, status: 'verlopen', expiredReason: 'Winkelgebied gewijzigd', expiredAt: now }
          : d
      );
      toastMsg = `⏰ ${areaChangeDeals.all.length} deals verlopen`;
    } else if (action === 'delete') {
      updatedDeals = allDeals.map((d) =>
        matches(d)
          ? { ...d, status: 'verwijderd', deletedReason: 'Winkelgebied gewijzigd', deletedAt: now }
          : d
      );
      toastMsg = `🗑️ ${areaChangeDeals.all.length} deals verwijderd`;
    }

    localStorage.setItem('walkin_shop_deals', JSON.stringify(updatedDeals));
    window.dispatchEvent(new Event('walkin-deals-change'));

    logActivity('Winkelgebied gewijzigd', {
      shop: shop.shopName,
      oldArea,
      newArea,
      dealsAction: action,
      dealsCount: areaChangeDeals.all.length,
    });

    setShowAreaModal(false);
    if (toastMsg) showToast(toastMsg);
    await commitSave();
  };

  const handleAreaCancel = () => {
    setFormData((f) => ({ ...f, area: shop.area }));
    setShowAreaModal(false);
  };

  const handleResetPassword = () => {
    const pwd = pwdMode === 'generated' ? generatePassword() : pwdManual.trim();
    if (!pwd) return;
    updateShopInBoth(id, shop.email, { password: pwd });
    logActivity('Wachtwoord gereset', { shop: shop.shopName, id });
    setPwdResult(pwd);
    setPwdModal(false);
    setPwdManual('');
  };

  // Blocking also pauses every running deal of the shop straight away.
  const handleBlock = async () => {
    try {
      const { paused, failed } = await setRetailerBlocked({ ...shop, id }, true);
      logActivity('Account geblokkeerd', {
        shop: shop.shopName,
        reason: blockReason,
        from: shop.status || 'actief',
        to: 'geblokkeerd',
      });
      setBlockModal(false);
      setBlockReason('');
      showToast(
        failed
          ? `🚫 ${shop.shopName} geblokkeerd — let op: niet alle deals konden worden gepauzeerd`
          : `🚫 ${shop.shopName} geblokkeerd — ${paused} ${paused === 1 ? 'deal' : 'deals'} direct gestopt`,
        'error'
      );
      await loadShop();
    } catch (err) {
      showToast(err.message || 'Blokkeren mislukt', 'error');
    }
  };

  const handleUnblock = async () => {
    try {
      await setRetailerActive(id, true);
      logActivity('Account gedeblokkeerd', {
        shop: shop.shopName,
        from: shop.status,
        to: 'actief',
      });
      showToast(`✅ ${shop.shopName} geactiveerd — gepauzeerde deals kan de winkelier zelf weer activeren`);
      await loadShop();
    } catch (err) {
      showToast(err.message || 'Activeren mislukt', 'error');
    }
  };

  const handleDelete = () => {
    updateShopInBoth(id, shop.email, {
      status: 'verwijderd',
      deletedAt: new Date().toISOString(),
    });
    logActivity('Account verwijderd (soft)', { shop: shop.shopName });
    setDeleteStep(0);
    setDeleteTypeIn('');
    showToast(`🗑️ ${shop.shopName} verwijderd`, 'error');
    setTimeout(() => navigate('/admin/shops'), 700);
  };

  const areaList = [...areaLookup.values()].sort((a, b) => a.name.localeCompare(b.name));
  const shopArea = (shop.areaId && areaLookup.get(shop.areaId))
    || areaList.find((a) => a.name === shop.area) || null;
  const areaOptions = areaList.some((a) => a.name === formData.area) || !formData.area
    ? areaList.map((a) => a.name)
    : [formData.area, ...areaList.map((a) => a.name)];

  const hours = formData.openingHours || DEFAULT_HOURS;
  const viewHours = shop.openingHours || DEFAULT_HOURS;
  const isUnlimited = shop.subscription === 'unlimited';

  return (
    <div>
      {toast && (
        <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
      )}

      <header className="admin-page-head">
        <div>
          <button className="admin-link" onClick={() => navigate(-1)}>← Terug</button>
          <h1 className="admin-page-title" style={{ marginTop: 6 }}>Winkelier details</h1>
          <p className="admin-page-sub">
            <span className={`admin-badge admin-badge--${shop.status || 'actief'}`}>
              {(shop.status || 'actief').replace('_', ' ')}
            </span>
            &nbsp;·&nbsp;Lid sinds {formatDate(shop.createdAt)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isEditing && (
            <button className="admin-btn admin-btn--orange" onClick={handleEdit}>
              ✏️ Bewerken
            </button>
          )}
          {isEditing && (
            <>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={handleCancel}
                disabled={saving}
              >
                ✗ Annuleren
              </button>
              <button
                className="admin-btn"
                style={{ background: '#16A34A' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Opslaan…' : '💾 Opslaan'}
              </button>
            </>
          )}
        </div>
      </header>

      {error && (
        <div style={{
          background: '#FEE2E2', color: '#991B1B', padding: 10, borderRadius: 10,
          marginBottom: 14, fontSize: 13, fontWeight: 600,
        }}>{error}</div>
      )}

      {/* WINKEL INFO ------------------------------------------------------- */}
      <div className="admin-card">
        <div className="admin-card__title">Winkel info</div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Winkelnaam</span>
            {isEditing ? (
              <input className="admin-input" value={formData.shopName}
                onChange={(e) => set('shopName', e.target.value)} />
            ) : (
              <div>{shop.shopName}</div>
            )}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">User ID</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="admin-mono">{shop.id || '—'}</span>
              <button className="admin-link" onClick={() => copy(shop.id, 'User ID')}>
                Kopieer
              </button>
            </div>
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">E-mailadres</span>
            {isEditing ? (
              <input className="admin-input" type="email" value={formData.email}
                onChange={(e) => set('email', e.target.value)} />
            ) : (
              <div>{shop.email}</div>
            )}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Gebiedscode</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{
                background: '#FFEDD5', color: '#C2410C', padding: '4px 10px',
                borderRadius: 100, fontWeight: 700, fontSize: 12,
              }}>{shopArea?.code || '—'}</span>
              {shopArea?.code && (
                <button className="admin-link" onClick={() => copy(shopArea.code, 'Gebiedscode')}>
                  Kopieer
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Winkelgebied</span>
          {isEditing ? (
            <select className="admin-select" value={formData.area}
              onChange={(e) => set('area', e.target.value)}>
              <option value="">Kies een gebied…</option>
              {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          ) : (
            <div>{shop.area || '—'}</div>
          )}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Gebiedsadres</span>
          <div style={{ color: shopArea?.address ? undefined : '#9CA3AF' }}>
            {shopArea?.address || 'Nog geen gebiedsadres — stel dit in bij Gebieden'}
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Categorie (branche)</span>
          {isEditing ? (
            <>
              <select className="admin-select" value={formData.category || ''}
                onChange={(e) => set('category', e.target.value)}>
                <option value="">Kies een categorie…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="admin-field__hint">
                Deals van deze winkel nemen de categorie automatisch over.
              </span>
            </>
          ) : (
            <div>{shop.category || '—'}</div>
          )}
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Winkeladres</span>
            {isEditing ? (
              <input className="admin-input" value={formData.address}
                onChange={(e) => set('address', e.target.value)} />
            ) : (
              <div>{shop.address || '—'}</div>
            )}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Postcode</span>
            {isEditing ? (
              <input className="admin-input" value={formData.postcode}
                onChange={(e) => set('postcode', e.target.value)} />
            ) : (
              <div>{shop.postcode || '—'}</div>
            )}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Stad</span>
            {isEditing ? (
              <input className="admin-input" value={formData.city}
                onChange={(e) => set('city', e.target.value)} />
            ) : (
              <div>{shop.city || '—'}</div>
            )}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Status</span>
            {isEditing ? (
              <select className="admin-select" value={formData.status}
                onChange={(e) => set('status', e.target.value)}>
                <option value="actief">Actief</option>
                <option value="inactief">Inactief</option>
                <option value="geblokkeerd">Geblokkeerd</option>
              </select>
            ) : (
              <span className={`admin-badge admin-badge--${shop.status || 'actief'}`}>
                {(shop.status || 'actief').replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Beschrijving</span>
          {isEditing ? (
            <textarea className="admin-input" rows={3} value={formData.description}
              onChange={(e) => set('description', e.target.value)} />
          ) : (
            <div>{shop.description || '—'}</div>
          )}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Looproute instructies (optioneel)</span>
          {isEditing ? (
            <>
              <textarea
                className="admin-input"
                rows={3}
                maxLength={300}
                placeholder="bijv: Ingang Noord · Verdieping 1 · Naast de H&M"
                value={formData.instructions}
                onChange={(e) => set('instructions', e.target.value.slice(0, 300))}
              />
              <span className="admin-field__hint" style={{ textAlign: 'right' }}>
                {(formData.instructions || '').length}/300
              </span>
            </>
          ) : (
            <div>{shop.instructions || '—'}</div>
          )}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Exacte locatie (GPS)</span>
          {isEditing ? (
            <LocationSearchPanel
              formData={formData}
              onPatch={(patch) => setFormData((f) => ({ ...f, ...patch }))}
            />
          ) : shop.lat && shop.lng ? (
            <div>
              <span className="admin-mono">
                {parseFloat(shop.lat).toFixed(6)}, {parseFloat(shop.lng).toFixed(6)}
              </span>
              {shop.locationAddress && (
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  {shop.locationAddress}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#9CA3AF' }}>Geen GPS coördinaten ingesteld</div>
          )}
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Lid sinds</span>
            <div>{formatDate(shop.createdAt)}</div>
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Laatste login</span>
            <div>{formatDateTime(shop.lastLogin)}</div>
          </div>
        </div>
      </div>

      {/* ABONNEMENT -------------------------------------------------------- */}
      <div className="admin-card">
        <div className="admin-card__title">Abonnement</div>
        {isEditing ? (
          <>
            <div className="admin-field">
              <span className="admin-field__label">Type</span>
              <select className="admin-select" value={formData.subscription}
                onChange={(e) => set('subscription', e.target.value)}>
                <option value="tokens">Tokens</option>
                <option value="unlimited">Unlimited</option>
              </select>
            </div>
            {formData.subscription === 'unlimited' ? (
              <div className="admin-form-row">
                <div className="admin-field">
                  <span className="admin-field__label">Unlimited tot en met (optioneel)</span>
                  <input className="admin-input" type="date"
                    value={formData.unlimitedUntil || ''}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => set('unlimitedUntil', e.target.value)} />
                  <span className="admin-field__hint">
                    Leeg = doorlopend. Handig voor de pilot: zet hier de einddatum,
                    daarna valt de winkel automatisch terug op tokens.
                  </span>
                </div>
                <div className="admin-field">
                  <span className="admin-field__label">Token-saldo</span>
                  <div style={{ paddingTop: 8 }}>
                    {Number(shop.tokensAvailable) || 0} tokens blijven bewaard voor na Unlimited
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-form-row">
                <div className="admin-field">
                  <span className="admin-field__label">Tokens beschikbaar (saldo)</span>
                  <input className="admin-input" type="number" min={0}
                    value={formData.tokensAvailable}
                    onChange={(e) => set('tokensAvailable', e.target.value)} />
                  <span className="admin-field__hint">
                    Wordt direct in de database opgeslagen en is meteen zichtbaar voor de winkelier.
                  </span>
                </div>
                <div className="admin-field">
                  <span className="admin-field__label">Gebruikt / totaal toegekend</span>
                  <div style={{ paddingTop: 8 }}>
                    {Number(shop.tokensUsed) || 0} gebruikt ·{' '}
                    {(Number(shop.tokensUsed) || 0) + (Math.max(0, parseInt(formData.tokensAvailable, 10) || 0))} totaal
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="admin-grid">
            <div className="admin-stat">
              <span className="admin-stat__label">Type</span>
              <span className="admin-stat__value" style={{ fontSize: 18 }}>
                {isUnlimited
                  ? <span className="admin-badge admin-badge--unlimited">
                      Unlimited{shop.subscription_end_date ? ` t/m ${formatDate(shop.subscription_end_date)}` : ''}
                    </span>
                  : <span className="admin-badge admin-badge--starter">Tokens</span>}
              </span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat__label">Gekocht</span>
              <span className="admin-stat__value">{isUnlimited ? '∞' : shop.tokensPurchased ?? 0}</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat__label">Beschikbaar</span>
              <span className="admin-stat__value">{isUnlimited ? '∞' : shop.tokensAvailable ?? 0}</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat__label">Gebruikt</span>
              <span className="admin-stat__value">{shop.tokensUsed ?? 0}</span>
            </div>
          </div>
        )}
      </div>

      {/* ABONNEMENTSSTATUS (alleen lezen) ---------------------------------- */}
      <div className="admin-card">
        <div className="admin-card__title">Abonnementsstatus</div>
        <p className="admin-field__hint" style={{ marginTop: 0 }}>
          Alleen ter informatie — opzeggen doet de winkelier zelf via het
          winkelierportaal.
        </p>
        <div className="admin-grid">
          <div className="admin-stat">
            <span className="admin-stat__label">Huidig plan</span>
            <span className="admin-stat__value" style={{ fontSize: 15 }}>
              {SUBSCRIPTION_PLAN_LABELS[shop.subscription_plan]
                || (isUnlimited ? 'Unlimited' : 'Starter')}
            </span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat__label">Gestart op</span>
            <span className="admin-stat__value" style={{ fontSize: 15 }}>
              {formatDate(shop.subscription_start_date)}
            </span>
          </div>
          {shop.subscription_cancelled_at && (
            <>
              <div className="admin-stat">
                <span className="admin-stat__label">Opgezegd op</span>
                <span className="admin-stat__value" style={{ fontSize: 15 }}>
                  {formatDate(shop.subscription_cancelled_at)}
                </span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat__label">Actief tot</span>
                <span className="admin-stat__value" style={{ fontSize: 15 }}>
                  {formatDate(shop.subscription_end_date)}
                </span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat__label">Valt terug op</span>
                <span className="admin-stat__value" style={{ fontSize: 15 }}>
                  {SUBSCRIPTION_PLAN_LABELS[shop.fallback_plan] || 'Starter'}
                </span>
              </div>
            </>
          )}
        </div>
        {!shop.subscription_cancelled_at && (
          <p className="admin-field__hint">Niet opgezegd.</p>
        )}
      </div>

      {/* OPENINGSTIJDEN ---------------------------------------------------- */}
      <div className="admin-card">
        <div className="admin-card__title">Openingstijden</div>
        {DAYS.map((d) => {
          const slot = isEditing ? (hours[d.key] || { open: '', close: '', closed: false })
                                 : (viewHours[d.key] || { open: '', close: '', closed: false });
          return (
            <div key={d.key} style={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 1fr 100px',
              gap: 10, alignItems: 'center', padding: '6px 0',
              borderBottom: '1px solid #F1F2F4', fontSize: 13,
            }}>
              <strong>{d.label}</strong>
              {isEditing ? (
                <>
                  <input type="time" className="admin-input" value={slot.open || ''}
                    disabled={slot.closed}
                    onChange={(e) => setHour(d.key, { open: e.target.value })} />
                  <input type="time" className="admin-input" value={slot.close || ''}
                    disabled={slot.closed}
                    onChange={(e) => setHour(d.key, { close: e.target.value })} />
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={!!slot.closed}
                      onChange={(e) => setHour(d.key, { closed: e.target.checked })} />
                    Gesloten
                  </label>
                </>
              ) : slot.closed ? (
                <span style={{ color: '#9CA3AF', fontStyle: 'italic', gridColumn: 'span 3' }}>
                  Gesloten
                </span>
              ) : (
                <>
                  <span>{slot.open || '—'}</span>
                  <span>{slot.close || '—'}</span>
                  <span />
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* DEALS + INVOICES (read-only context) ------------------------------ */}
      {shopDeals.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__title">Deals ({shopDeals.length})</div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Deal</th><th>Status</th><th>Views</th><th>Inleveringen</th></tr>
              </thead>
              <tbody>
                {shopDeals.map((d) => (
                  <tr key={d.id}>
                    <td>{d.emoji} {d.title}</td>
                    <td>
                      <span className={`admin-badge admin-badge--${d.status || 'actief'}`}>
                        {d.status || 'actief'}
                      </span>
                    </td>
                    <td>{d.views || 0}</td>
                    <td>{d.redemptions || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {shopInvoices.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__title">Facturen ({shopInvoices.length})</div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Nummer</th><th>Pakket</th><th>Totaal</th><th>Datum</th><th>Status</th></tr>
              </thead>
              <tbody>
                {shopInvoices.map((inv) => (
                  <tr key={inv.id}>
                    <td><span className="admin-mono">{inv.number}</span></td>
                    <td>{inv.type}</td>
                    <td>€{inv.total.toFixed(2)}</td>
                    <td>{formatDate(inv.date)}</td>
                    <td>
                      <span className={`admin-badge admin-badge--${inv.status.toLowerCase()}`}>
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ACCOUNT ACTIES ---------------------------------------------------- */}
      <div className="admin-card">
        <div className="admin-card__title">Account acties</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="admin-btn admin-btn--ghost" onClick={() => setPwdModal(true)}>
            🔑 Reset wachtwoord
          </button>
          {shop.status === 'geblokkeerd' ? (
            <button className="admin-btn" style={{ background: '#16A34A' }} onClick={handleUnblock}>
              ✅ Activeer account
            </button>
          ) : (
            <button className="admin-btn admin-btn--danger" onClick={() => setBlockModal(true)}>
              🚫 Blokkeer account
            </button>
          )}
          <button className="admin-btn admin-btn--danger" onClick={() => setDeleteStep(1)}>
            🗑️ Verwijder account
          </button>
        </div>
      </div>

      {/* PASSWORD RESET MODAL --------------------------------------------- */}
      {pwdModal && (
        <div className="admin-modal" role="dialog" onClick={() => setPwdModal(false)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Wachtwoord resetten</h3>
            <p className="admin-modal__text">Kies hoe je het wachtwoord wilt instellen.</p>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <input type="radio" name="pwdmode" checked={pwdMode === 'generated'}
                onChange={() => setPwdMode('generated')} />
              Genereer nieuw wachtwoord (WI-XXXXXX)
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <input type="radio" name="pwdmode" checked={pwdMode === 'manual'}
                onChange={() => setPwdMode('manual')} />
              Stel zelf in
            </label>
            {pwdMode === 'manual' && (
              <input className="admin-input" placeholder="Nieuw wachtwoord"
                value={pwdManual}
                onChange={(e) => setPwdManual(e.target.value)} />
            )}

            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setPwdModal(false)}>
                Annuleren
              </button>
              <button
                className="admin-btn admin-btn--orange"
                disabled={pwdMode === 'manual' && !pwdManual.trim()}
                onClick={handleResetPassword}
              >
                Reset wachtwoord
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PASSWORD RESULT MODAL -------------------------------------------- */}
      {pwdResult && (
        <div className="admin-modal" role="dialog">
          <div className="admin-modal__card">
            <h3 className="admin-modal__title">✅ Wachtwoord gereset!</h3>
            <p className="admin-modal__text">
              Stuur dit naar {shop.shopName} via een veilig kanaal.
            </p>
            <div style={{
              background: '#F1F5F9', padding: 14, borderRadius: 12,
              fontSize: 20, fontWeight: 800, letterSpacing: 2, textAlign: 'center',
              color: '#1A3E5C',
            }}>{pwdResult}</div>
            <div className="admin-modal__actions">
              <button
                className="admin-btn"
                onClick={() => { copy(pwdResult, 'Wachtwoord'); setPwdResult(null); }}
              >
                📋 Kopieer wachtwoord
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => setPwdResult(null)}>
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BLOCK MODAL ------------------------------------------------------ */}
      {blockModal && (
        <div className="admin-modal" role="dialog" onClick={() => setBlockModal(false)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Blokkeer {shop.shopName}?</h3>
            <p className="admin-modal__text">
              De winkelier kan na blokkering niet meer inloggen totdat een admin
              het account weer activeert. <strong>Alle lopende deals van deze
              winkel worden direct gestopt</strong> (gepauzeerd) en zijn niet
              meer zichtbaar voor bezoekers.
            </p>
            <textarea className="admin-input" rows={3}
              placeholder="Reden (optioneel)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)} />
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setBlockModal(false)}>
                Annuleren
              </button>
              <button className="admin-btn admin-btn--danger" onClick={handleBlock}>
                Blokkeer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL (two steps) ----------------------------------------- */}
      {deleteStep === 1 && (
        <div className="admin-modal" role="dialog" onClick={() => setDeleteStep(0)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Winkelier verwijderen?</h3>
            <p className="admin-modal__text">
              Dit kan niet ongedaan worden gemaakt. De data blijft voor audit
              behouden maar het account wordt op verwijderd gezet.
            </p>
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setDeleteStep(0)}>
                Annuleren
              </button>
              <button className="admin-btn admin-btn--danger" onClick={() => setDeleteStep(2)}>
                Doorgaan
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep === 2 && (
        <div className="admin-modal" role="dialog" onClick={() => setDeleteStep(0)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Type de winkelnaam om te bevestigen</h3>
            <p className="admin-modal__text">
              Type "<strong>{shop.shopName}</strong>" om definitief te verwijderen.
            </p>
            <input className="admin-input"
              placeholder={shop.shopName}
              value={deleteTypeIn}
              onChange={(e) => setDeleteTypeIn(e.target.value)} />
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost"
                onClick={() => { setDeleteStep(0); setDeleteTypeIn(''); }}>
                Annuleren
              </button>
              <button
                className="admin-btn admin-btn--danger"
                disabled={deleteTypeIn !== shop.shopName}
                onClick={handleDelete}
              >
                Definitief verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AREA-CHANGE WARNING MODAL --------------------------------------- */}
      {showAreaModal && (
        <div className="admin__modal" role="dialog">
          <div className="admin__modal-card">
            <div className="admin__modal-icon">⚠️</div>
            <h3>Winkelgebied wijzigen</h3>
            <p>
              <strong>{shop.shopName}</strong> heeft nog deals in <strong>{shop.area}</strong>:
            </p>

            <div className="admin__modal-stats">
              <div>🟢 {areaChangeDeals.active.length} actieve deals</div>
              <div>⏸ {areaChangeDeals.paused.length} gepauzeerde deals</div>
              <div>📍 Huidig gebied: {shop.area}</div>
              <div>📍 Nieuw gebied: {formData.area}</div>
            </div>

            <p className="admin__modal-question">
              Wat moet er gebeuren met de bestaande deals?
            </p>

            <div className="admin__modal-options">
              <button
                className="admin__modal-option"
                onClick={() => handleAreaChange('move')}
              >
                <span className="option-icon">📦</span>
                <div>
                  <strong>Verplaats deals mee</strong>
                  <p>
                    Alle deals worden verplaatst naar {formData.area}. Deals blijven actief.
                  </p>
                </div>
              </button>

              <button
                className="admin__modal-option admin__modal-option--warning"
                onClick={() => handleAreaChange('expire')}
              >
                <span className="option-icon">⏰</span>
                <div>
                  <strong>Deals laten verlopen</strong>
                  <p>
                    Bestaande deals worden gemarkeerd als verlopen. Winkelier moet nieuwe deals aanmaken.
                  </p>
                </div>
              </button>

              <button
                className="admin__modal-option admin__modal-option--danger"
                onClick={() => handleAreaChange('delete')}
              >
                <span className="option-icon">🗑️</span>
                <div>
                  <strong>Deals verwijderen</strong>
                  <p>
                    Alle bestaande deals worden permanent verwijderd.
                  </p>
                </div>
              </button>
            </div>

            <button
              className="admin__modal-cancel"
              onClick={handleAreaCancel}
            >
              Annuleren — gebied niet wijzigen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
